import { ITimetableReportItem } from '@pages/Journal2/components/Tabs/KTPJournal'
import { FC, useEffect, useMemo, useState } from 'react'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import moment from 'moment'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import { useDispatch, useSelector } from 'react-redux'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import PrimaryButton from '@pages/Admin/CourseSettings/components/PrimaryButton/PrimaryButton'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import { makeEnteringToClassroom } from '../../utils/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import cn from './index.module.css'
import classNames from 'classnames'
import { fieldIsPaidInReport } from '@/constant'
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { conferenceTypesEnum } from '@modals/Timetable/AddLessonOrMeeting/components/ConferenceType/conferenceTypesEnum'
import { Link } from 'react-router-dom'
import { fetchTimetableData } from '@store/timetable/fetchTimetableData'
import {
  getLessonStatusOptions,
  isClosedCustomizableLesson,
} from '@pages/Main/LessonReports/helpers/getLessonStatusOptions'
import timetableReportStatusEnum from '@/constant/timetableReportStatusEnum'

import { isTeacherLessonReportJournalRestrictionApplicable } from '@/helpers/teacherLessonReportJournalScope'
interface ILessonReportCard {
  report: ITimetableReportItem
  onSave: (status: string, duration: number) => Promise<void>
  isSelected?: boolean
  selectionType?: 'group' | 'individual'
  onSelect?: (duration: number, report: ITimetableReportItem) => void
  initialForm?: {
    duration: number
    opened_at: string | null
    status: string | null
  }
  onFormChange?: (value: {
    duration: number
    opened_at: string | null
    status: string | null
  }) => void
}

const LessonReportCard: FC<ILessonReportCard> = ({
  report,
  onSave,
  isSelected,
  selectionType,
  onSelect,
  initialForm,
  onFormChange,
}) => {
  const dispatch = useDispatch()
  const addAlert = useAddAlert()
  const [isSaving, setIsSaving] = useState(false)
  const isClosedCustomizable = isClosedCustomizableLesson(report)
  const settings = useSelector((store: any) => {
    const settings = store.settings

    return {
      canChangeDuration: settings?.isChangeDuration === '1',
      isTeacherArrivalTimeForTheMeeting:
        settings?.isTeacherArrivalTimeForTheMeeting === '1',
      allCheckedRule: isTeacherLessonReportJournalRestrictionApplicable(settings, report),
      isTeacherCanRescheduleGroupLessons:
        settings?.isTeacherCanRescheduleGroupLessons !== '0',
      isTeacherCanRescheduleIndividualLessons:
        settings?.isTeacherCanRescheduleIndividualLessons !== '0',
    }
  })

  const [form, setFrom] = useState<{
    duration: number
    opened_at: string | null
    status: string | null
  }>({
    duration: initialForm?.duration ?? report?.duration ?? 0,
    opened_at: initialForm?.opened_at ?? report.opened_at,
    status: initialForm?.status ?? null,
  })

  useEffect(() => {
    setFrom({
      duration: initialForm?.duration ?? report?.duration ?? 0,
      opened_at: initialForm?.opened_at ?? report.opened_at,
      status: initialForm?.status ?? null,
    })
  }, [
    initialForm?.duration,
    initialForm?.opened_at,
    initialForm?.status,
    report?.duration,
    report?.id,
    report?.opened_at,
  ])

  const updateForm = (
    updater: (prev: {
      duration: number
      opened_at: string | null
      status: string | null
    }) => {
      duration: number
      opened_at: string | null
      status: string | null
    },
  ) => {
    setFrom((prev) => {
      const next = updater(prev)
      onFormChange?.(next)

      return next
    })
  }

  const startDateAndTime = moment(
    `${report.date_conference} ${report.start_time}`,
  ).format('DD.MM.YYYY [в] HH:mm')

  const dateTimeEntryIntoClass = form.opened_at
    ? moment(form.opened_at).format('DD.MM.YYYY [в] HH:mm')
    : 'Не входили'

  const enterClassroom = async () => {
    if (
      !settings.isTeacherArrivalTimeForTheMeeting ||
      form?.opened_at !== null
    ) {
      return
    }

    const res = await makeEnteringToClassroom(report)
    updateForm((prev) => {
      return { ...prev, opened_at: res }
    })
  }

  const options = useMemo(() => {
    const isGroupPerLesson = report.direction_tariff === 'group_per_lesson'
    const isGroupReport = report.conference_type === 'group' || isGroupPerLesson
    const isIntroductionReport = report.conference_type === 'introduction'

    // Перенос вводного занятия предусмотрен ТЗ и не зависит от настроек переноса
    // групповых/индивидуальных занятий. Вводное, привязанное к групповому,
    // переносится только вместе с ним — там перенос недоступен.
    const canRescheduleCurrentReport = isIntroductionReport
      ? !report?.parent_event_id
      : isGroupReport
      ? settings?.isTeacherCanRescheduleGroupLessons
      : settings?.isTeacherCanRescheduleIndividualLessons

    return getLessonStatusOptions({
      conferenceType: report.conference_type,
      directionTariff: report.direction_tariff,
      hasPaidCancel: report?.has_paid_cancel,
      canRescheduleCurrentReport,
      isOpen: Number(report?.isOpen) === 1,
      isClosedCustomizable,
      hasOpenLessonAttendees:
        Number(report?.isOpen) !== 1 || (report?.attendees?.length ?? 0) > 0,
    })
  }, [
    report.conference_type,
    report.direction_tariff,
    report.has_paid_cancel,
    report.parent_event_id,
    report.isOpen,
    report.attendees?.length,
    isClosedCustomizable,
    settings?.isTeacherCanRescheduleGroupLessons,
    settings?.isTeacherCanRescheduleIndividualLessons,
  ])

  const participants = useMemo(() => {
    if (report?.isOpen || isClosedCustomizable) {
      return report?.title || ' - '
    }

    return report?.conference_type === 'group'
      ? report?.groupsText?.join(', ') ?? ' - '
      : report?.attendeesText?.join(', ') ?? ' - '
  }, [report, isClosedCustomizable])

  const lessonTypeLabel = isClosedCustomizable
    ? 'Настраиваемый:'
    : report?.isOpen
    ? 'Открытое занятие:'
    : report?.conference_type === conferenceTypesEnum.introduction
    ? 'Вводное:'
    : report?.conference_type === 'group'
    ? 'Группа:'
    : 'Индивидуальные:'

  const participantsLabel = report?.isOpen || isClosedCustomizable
    ? 'Название занятия'
    : report?.conference_type === 'group'
    ? 'Группы'
    : 'Учащиеся'

  const canSave = useMemo(() => {
    if (isSaving) return false
    if (report.conference_type !== 'group') return true
    if (form.status === 'reschedule') return true
    if (report.direction_tariff === 'group_per_lesson') return true
    if (settings?.allCheckedRule && form.status === '5')
      return report.all_children_graded
    return true
  }, [report, form, settings, isSaving])

  const saveHandle = async () => {
    setIsSaving(true)
    if (!form.status) {
      addAlert({ type: 'error', title: 'Выберите статус' })
      setIsSaving(false)
      return
    }
    await onSave(form.status, form.duration)
    dispatch(fetchTimetableData())
    setIsSaving(false)
  }

  const cantSelect =
    selectionType === undefined
      ? false
      : report?.conference_type === 'group'
      ? selectionType !== 'group'
      : selectionType === 'group'

  const hidePaymentStatus = [
    timetableReportStatusEnum.canceledStudent,
    timetableReportStatusEnum.canceledSeminarian,
  ].includes(Number(report?.status))

  const selectTooltipTitle = useMemo(() => {
    if (selectionType === undefined || !canSave) {
      return !canSave
        ? 'Проставьте всем учащимся статус в Эл.журе и после этого отчитайтесь по занятию'
        : ''
    }

    if (cantSelect) {
      return selectionType === 'group'
        ? 'Вы можете массово отметить или множество групповых или множество индивидуальных занятий. Сейчас вы массово отмечаете групповые встречи'
        : 'Вы можете массово отметить или множество групповых или множество индивидуальных занятий. Сейчас вы массово отмечаете индивидуальные встречи'
    }

    return ''
  }, [report, selectionType, cantSelect, canSave])

  return (
    <div className={classNames(cn.wrapper, isSelected && cn.cardSelected)}>
      <div className={cn.rowBetween}>
        <FieldWrapper label="Начало">
          <TooltipWrapper title={startDateAndTime}>
            <div className={cn.fieldWrapper} style={{ textAlign: 'center' }}>
              {startDateAndTime}
            </div>
          </TooltipWrapper>
        </FieldWrapper>
        <FieldWrapper label="Курс">
          <TooltipWrapper title={report?.direction ?? ' - '}>
            <div className={cn.fieldWrapper}>{report?.direction ?? ' - '}</div>
          </TooltipWrapper>
        </FieldWrapper>
        <TextField
          label="Длительность (мин)"
          value={String(form.duration)}
          type="number"
          disabled={!settings.canChangeDuration || isSelected}
          onChange={(value) =>
            updateForm((prev) => {
              return { ...prev, duration: Number(value) }
            })
          }
        />
      </div>
      <div className={cn.rowMinMax}>
        <div className={cn.fieldWrapper}>
          {lessonTypeLabel}
        </div>
        <TooltipWrapper
          title={`${participantsLabel}: ${participants}`}
        >
          <div className={cn.fieldWrapper}>{participants}</div>
        </TooltipWrapper>
      </div>
      {report.link_to_meeting && (
        <div className={cn.rowMinMax}>
          <a
            target="_blank"
            rel="noreferrer"
            href={report.link_to_meeting ?? ''}
            style={{ alignSelf: 'flex-end' }}
          >
            <SecondaryButton
              className={cn.buttonFollow}
              onClick={enterClassroom}
            >
              Присоединиться
            </SecondaryButton>
          </a>
          {settings.isTeacherArrivalTimeForTheMeeting && (
            <FieldWrapper label="Время входа">
              <TooltipWrapper title={dateTimeEntryIntoClass}>
                <div className={cn.fieldWrapper}>{dateTimeEntryIntoClass}</div>
              </TooltipWrapper>
            </FieldWrapper>
          )}
        </div>
      )}

      <div className={cn.rowMaxMin}>
        <div className={cn.flexWrapper}>
          {
            !hidePaymentStatus && !report?.[fieldIsPaidInReport] && report.conference_type === conferenceTypesEnum.private && (
              <TooltipWrapper title='Не оплачено'>
                <ExclamationCircleOutlined
                  style={{ color: 'var(--color-danger)' }}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </TooltipWrapper>
            )}
          <SelectField
            placeholder="Выберите статус"
            value={form.status}
            options={options}
            disabled={!!onSelect}
            onChange={(value) => {
              if (typeof value !== 'string') {
                return
              }
              updateForm((prev) => {
                return { ...prev, status: value }
              })
            }}
          />
          {Number(report?.isOpen) === 1 && (
            <TooltipWrapper title="«Отменено» — деньги вернутся учащимся. «Платная отмена» — деньги не возвращаются.">
              <InfoCircleOutlined
                style={{ color: 'var(--color-accent)' }}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </TooltipWrapper>
          )}
        </div>

        {onSelect ? (
          <TooltipWrapper title={selectTooltipTitle}>
            <PrimaryButton
              className={classNames(
                cn.buttonSave,
                !isSelected && cn.buttonSecondary,
              )}
              onClick={() => onSelect?.(form.duration, report)}
              disabled={!canSave || cantSelect}
            >
              {isSelected ? 'Отменить' : 'Выбрать'}
            </PrimaryButton>
          </TooltipWrapper>
        ) : (
          <TooltipWrapper
            title={
             <>
             {
               !form.status
                ? 'Выберите статус'
                : !canSave
                ? <span>Проставьте всем учащимся статус в Эл.журе и после этого отчитайтесь по занятию
                    <Link style={{ color: 'var(--color-accent)', textDecoration: 'underline', marginLeft: '5px'}} to={'/v2/journal'}>Перейти в элжур</Link>
                  </span>
                : ''
             }
             </>
            }
          >
            <PrimaryButton
              className={cn.buttonSave}
              onClick={saveHandle}
              disabled={!canSave || !form.status}
              isLoading={isSaving}
            >
              Сохранить
            </PrimaryButton>
          </TooltipWrapper>
        )}
      </div>
    </div>
  )
}

export default LessonReportCard
